import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Clock,
  Send,
  Upload,
  Award,
  User,
  MapPin,
  DollarSign,
  Shield,
  Star,
  Calendar,
  FileText,
  Briefcase,
  BarChart3,
  Eye,
  Phone,
  Mail,
  CheckCircle,
  X,
  Users,
  LayoutDashboard,
  MessageSquare,
  ArrowLeft,
  Search,
  Filter
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import VendorCard from './VendorCard';
import VendorFilters from './VendorFilters';
import VendorStatsCards from './VendorStatsCards';
import VendorToolbar from './VendorToolbar';
import VendorInviteModal from './VendorInviteModal';
import PendingVendorsSection from './PendingVendorsSection';
import VendorPerformanceDashboard from './VendorPerformanceDashboard';
import VendorDocumentManager from './VendorDocumentManager';
import VendorAnalyticsDashboard from './VendorAnalyticsDashboard';
import VendorBulkImport from './VendorBulkImport';
import VendorPortalDashboard from './VendorPortalDashboard';
import VendorComplianceTracker from './VendorComplianceTracker';
import VendorCommunicationCenter from './VendorCommunicationCenter';
import { vendorService } from '../utils/vendors/vendorService';
import { Vendor } from '../types/vendor';
import {
  getVendorAddress,
  getVendorAddressObject,
  getVendorServices,
  getJobsCompleted,
  getVendorRating,
  getReviewCount,
} from '../utils/vendorHelpers';
import { VENDOR_STATUS_CONFIG, FilterOption } from '../utils/vendorConstants';
import {
  sortVendors,
  paginateVendors,
  exportVendorsToCSV,
  downloadCSV,
  SortOption,
  SortDirection,
} from '../utils/vendorUtils';

type ModalType = 'invite' | 'details' | 'performance' | 'documents' | 'analytics' | 'bulk-import' | null;
type TabType = 'dashboard' | 'analytics' | 'all' | 'pending_approval' | 'pending_invites' | 'compliance' | 'communications';

interface VendorManagementProps {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  company: {
    id: string;
    name: string;
  };
  isDarkMode?: boolean;
  onClose?: () => void;
}

export default function VendorManagement({ user, company, isDarkMode = false, onClose }: VendorManagementProps) {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('all');
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  const itemsPerPage = 10;

  // Local helper: Get status badge for vendor
  const getStatusBadge = (status: Vendor['status']) => {
    const config: Record<Vendor['status'], { label: string; className: string }> = {
      active: { label: 'Active', className: isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700' },
      pending_approval: { label: 'Pending', className: isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700' },
      suspended: { label: 'Suspended', className: isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700' },
      rejected: { label: 'Rejected', className: isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-700' },
      inactive: { label: 'Inactive', className: isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-700' },
    };
    const { label, className } = config[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={className}>{label}</Badge>;
  };

  // Local helper: Get rating stars display
  const getRatingStars = (vendor: Vendor) => {
    const rating = getVendorRating(vendor);
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= Math.round(rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
          />
        ))}
        <span className={`ml-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{rating.toFixed(1)}</span>
      </div>
    );
  };

  // Calculate Vendor Stats
  const stats = useMemo(() => {
    const total = vendors.length;
    const active = vendors.filter(v => v.status === 'active').length;
    const pending = vendors.filter(v => v.status === 'pending_approval').length;

    // Calculate average rating for active vendors with ratings
    const ratedVendors = vendors.filter(v => v.status === 'active' && getVendorRating(v) !== undefined);
    const avgRating = ratedVendors.length > 0
      ? ratedVendors.reduce((sum, v) => sum + (getVendorRating(v) || 0), 0) / ratedVendors.length
      : 0;

    return { total, active, pending, avgRating };
  }, [vendors]);

  // Load Vendors
  const loadVendors = useCallback(async () => {
    if (!company?.id) return;
    setIsLoading(true);
    try {
      const data = await vendorService.getVendors();
      setVendors(data);
    } catch (error) {
      console.error('Error loading vendors:', error);
      toast.error('Failed to load vendors list');
    } finally {
      setIsLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Filtering and Sorting
  const filteredVendors = useMemo(() => {
    let result = [...vendors];

    // Filter by Tab (if applicable to list view)
    if (activeTab === 'pending_approval') {
      // Filter logic handled in render for this tab specifically
    }

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(v =>
        v.name.toLowerCase().includes(query) ||
        v.contactName?.toLowerCase().includes(query) ||
        v.email?.toLowerCase().includes(query)
      );
    }

    // Filter Dropdown
    if (selectedFilter !== 'all') {
      result = result.filter(v => v.status === selectedFilter);
    }

    // Sort
    return sortVendors(result, sortBy, sortDirection);
  }, [vendors, searchQuery, selectedFilter, sortBy, sortDirection]);

  const paginatedData = useMemo(() => {
    return paginateVendors(filteredVendors, currentPage, itemsPerPage);
  }, [filteredVendors, currentPage, itemsPerPage]);

  // Handlers
  const handleSortChange = (newSort: SortOption) => {
    if (sortBy === newSort) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSort);
      setSortDirection('asc');
    }
  };

  const handleDirectionToggle = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleExportCSV = () => {
    const csv = exportVendorsToCSV(filteredVendors);
    downloadCSV(csv, `vendors-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleApproveVendor = async (vendorId: string) => {
    try {
      await vendorService.approveVendor(vendorId, user.id, user.name);
      toast.success('Vendor approved');
      loadVendors();
    } catch (error) {
      console.error('Error approving vendor:', error);
      toast.error('Failed to approve vendor');
    }
  };

  const handleRejectVendor = async (vendorId: string) => {
    if (!confirm('Are you sure you want to reject this vendor?')) return;
    try {
      await vendorService.rejectVendor(vendorId, user.id, user.name);
      toast.success('Vendor rejected');
      loadVendors();
    } catch (error) {
      console.error('Error rejecting vendor:', error);
      toast.error('Failed to reject vendor');
    }
  };

  const handleViewDetails = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setActiveModal('details');
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>

      {/* Header */}
      <div className={`
          relative px-6 py-4 shrink-0 flex items-center justify-between
          ${isDarkMode
          ? 'bg-slate-900 border-b border-slate-800'
          : 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-sm text-white'
        }
        `}>
        <div className="flex items-center gap-3">
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className={isDarkMode ? 'text-white hover:bg-slate-800' : 'text-white hover:bg-white/20'}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>Vendor Management</h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-blue-100'}`}>Manage service vendors and track performance</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => setActiveModal('invite')}
            className={`${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white text-blue-600 hover:bg-blue-50'} shadow-sm border-0`}
          >
            <Send className="h-4 w-4 mr-2" />
            Invite Vendor
          </Button>
          <Button
            onClick={() => setActiveModal('bulk-import')}
            variant="outline"
            className={`${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : 'border-blue-400 text-blue-100 hover:bg-blue-600 hover:text-white bg-blue-600/50'}`}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
        </div>
      </div>

      {/* Action Toolbar & Stats (Dashboard View) */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">

        {/* Stats Cards - Only show on Dashboard or All tabs to avoid clutter */}
        {(activeTab === 'dashboard' || activeTab === 'all') && (
          <VendorStatsCards stats={stats} />
        )}

        {/* Tabs - Enterprise Navigation */}
        <div className={`flex space-x-1 border-b overflow-x-auto scrollbar-hide ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center space-x-2 px-4 py-3 font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'dashboard'
              ? (isDarkMode ? 'text-blue-400 border-blue-500 bg-blue-900/20' : 'text-blue-600 border-blue-600 bg-blue-50')
              : (isDarkMode ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50')
              }`}
          >
            <LayoutDashboard className="h-4 w-4" />
            <span>Dashboard</span>
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center space-x-2 px-4 py-3 font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'analytics'
              ? (isDarkMode ? 'text-blue-400 border-blue-500 bg-blue-900/20' : 'text-blue-600 border-blue-600 bg-blue-50')
              : (isDarkMode ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50')
              }`}
          >
            <BarChart3 className="h-4 w-4" />
            <span>Analytics</span>
          </button>

          <button
            onClick={() => setActiveTab('all')}
            className={`flex items-center space-x-2 px-4 py-3 font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'all'
              ? (isDarkMode ? 'text-blue-400 border-blue-500 bg-blue-900/20' : 'text-blue-600 border-blue-600 bg-blue-50')
              : (isDarkMode ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50')
              }`}
          >
            <Users className="h-4 w-4" />
            <span>All Vendors <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{vendors.length}</span></span>
          </button>
          <button
            onClick={() => setActiveTab('pending_approval')}
            className={`flex items-center space-x-2 px-4 py-3 font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'pending_approval'
              ? (isDarkMode ? 'text-orange-400 border-orange-500 bg-orange-900/20' : 'text-orange-600 border-orange-600 bg-orange-50')
              : (isDarkMode ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50')
              }`}
          >
            <Clock className="h-4 w-4" />
            <span>Pending <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-orange-900/40 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>{vendors.filter(v => v.status === 'pending_approval').length}</span></span>
          </button>
          <button
            onClick={() => setActiveTab('pending_invites')}
            className={`flex items-center space-x-2 px-4 py-3 font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'pending_invites'
              ? (isDarkMode ? 'text-purple-400 border-purple-500 bg-purple-900/20' : 'text-purple-600 border-purple-600 bg-purple-50')
              : (isDarkMode ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50')
              }`}
          >
            <Send className="h-4 w-4" />
            <span>Invitations</span>
          </button>
          <button
            onClick={() => setActiveTab('compliance')}
            className={`flex items-center space-x-2 px-4 py-3 font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'compliance'
              ? (isDarkMode ? 'text-green-400 border-green-500 bg-green-900/20' : 'text-green-600 border-green-600 bg-green-50')
              : (isDarkMode ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50')
              }`}
          >
            <Shield className="h-4 w-4" />
            <span>Compliance</span>
          </button>
          <button
            onClick={() => setActiveTab('communications')}
            className={`flex items-center space-x-2 px-4 py-3 font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'communications'
              ? (isDarkMode ? 'text-indigo-400 border-indigo-500 bg-indigo-900/20' : 'text-indigo-600 border-indigo-600 bg-indigo-50')
              : (isDarkMode ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50')
              }`}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Communications</span>
          </button>
        </div>

        {/* Content */}
        <div className="min-h-[500px]">
          <AnimatePresence mode="wait">
            {activeTab === 'dashboard' ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <VendorPortalDashboard
                  user={user}
                  company={company}
                  onNavigate={(section) => {
                    if (section === 'all') setActiveTab('all');
                    else if (section === 'pending_approval') setActiveTab('pending_approval');
                    else if (section === 'pending_invites') setActiveTab('pending_invites');
                    else if (section === 'invite') setActiveModal('invite');
                    else if (section === 'compliance') setActiveTab('compliance');
                    else if (section === 'analytics') setActiveTab('analytics');
                    else if (section === 'bulk-import') setActiveModal('bulk-import');
                    else if (section === 'payments') toast.info('Payments feature coming soon');
                    else if (section === 'communications') setActiveTab('communications');
                  }}
                />
              </motion.div>
            ) : activeTab === 'analytics' ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="animate-in fade-in duration-300">
                <VendorAnalyticsDashboard vendors={vendors} />
              </motion.div>
            ) : activeTab === 'compliance' ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <VendorComplianceTracker user={user} company={company} />
              </motion.div>
            ) : activeTab === 'communications' ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <VendorCommunicationCenter user={user} company={company} />
              </motion.div>
            ) : activeTab === 'pending_invites' ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PendingVendorsSection
                  companyId={company.id}
                  onVendorCompleted={loadVendors}
                />
              </motion.div>
            ) : activeTab === 'pending_approval' ? (
              // Pending Approval Tab
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                {vendors.filter(v => v.status === 'pending_approval').length === 0 ? (
                  <div className={`text-center py-16 rounded-xl border border-dashed ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-300'}`}>
                    <Clock className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`} />
                    <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No Pending Approvals</h3>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                      All vendor profiles have been reviewed. New submissions will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {vendors.filter(v => v.status === 'pending_approval').map(vendor => (
                      <Card key={vendor.id} className={`border-l-4 border-l-orange-500 shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between flex-wrap gap-4">
                            <div className="flex-1 min-w-[300px]">
                              <div className="flex items-center space-x-3 mb-3">
                                <div className={`h-12 w-12 rounded-lg flex items-center justify-center font-bold text-xl ${isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>
                                  {vendor.name.charAt(0)}
                                </div>
                                <div>
                                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{vendor.name}</h3>
                                  <div className="flex items-center gap-2 mt-1">
                                    {getStatusBadge(vendor.status)}
                                    {vendor.dbaName && (
                                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>DBA: {vendor.dbaName}</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  <User className="h-4 w-4 text-gray-400" />
                                  <span>{vendor.contactName}</span>
                                </div>
                                <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  <Phone className="h-4 w-4 text-gray-400" />
                                  <span>{vendor.phone}</span>
                                </div>
                                <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  <Mail className="h-4 w-4 text-gray-400" />
                                  <span>{vendor.email}</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                              <Button onClick={() => handleApproveVendor(vendor.id)} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
                                <CheckCircle className="h-4 w-4 mr-2" /> Approve
                              </Button>
                              <Button onClick={() => handleRejectVendor(vendor.id)} variant="outline" className={`w-full sm:w-auto ${isDarkMode ? 'text-red-400 border-red-900/50 hover:bg-red-900/20' : 'text-red-600 border-red-200 hover:bg-red-50'}`}>
                                <X className="h-4 w-4 mr-2" /> Reject
                              </Button>
                              <Button onClick={() => handleViewDetails(vendor)} variant="ghost" className={`w-full sm:w-auto ${isDarkMode ? 'text-gray-300 hover:bg-slate-800' : ''}`}>
                                <Eye className="h-4 w-4 mr-2" /> View Details
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              // All Vendors Tab
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                {/* Filters and Search */}
                <VendorFilters
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  selectedFilter={selectedFilter}
                  onFilterChange={setSelectedFilter}
                />

                {/* Toolbar */}
                {filteredVendors.length > 0 && (
                  <VendorToolbar
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSortChange={handleSortChange}
                    onDirectionToggle={handleDirectionToggle}
                    currentPage={currentPage}
                    totalPages={paginatedData.totalPages}
                    totalVendors={filteredVendors.length}
                    onPageChange={setCurrentPage}
                    onExportCSV={handleExportCSV}
                  />
                )}

                {/* Vendors List */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
                  </div>
                ) : filteredVendors.length === 0 ? (
                  <Card className={isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white'}>
                    <CardContent className="py-16 text-center">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <Users className={`h-10 w-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                      </div>
                      <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No vendors found</h3>
                      <p className={`mb-8 max-w-md mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {searchQuery || selectedFilter !== 'all'
                          ? `We couldn't find any vendors matching "${searchQuery}" with the current filters.`
                          : 'Get started by inviting your first service provider to the platform.'}
                      </p>
                      {!searchQuery && selectedFilter === 'all' && (
                        <Button onClick={() => setActiveModal('invite')} className="bg-blue-600 hover:bg-blue-700 h-12 px-6">
                          <Send className="h-5 w-5 mr-2" />
                          Invite New Vendor
                        </Button>
                      )}
                      {(searchQuery || selectedFilter !== 'all') && (
                        <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedFilter('all'); }}>
                          Clear Filters
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {paginatedData.vendors.map((vendor) => (
                      <VendorCard
                        key={vendor.id}
                        vendor={vendor}
                        onApprove={vendor.status === 'pending_approval' ? handleApproveVendor : undefined}
                        onReject={vendor.status === 'pending_approval' ? handleRejectVendor : undefined}
                        onViewDetails={handleViewDetails}
                        onViewPerformance={vendor.status === 'active' ? (v) => {
                          setSelectedVendor(v);
                          setActiveModal('performance');
                        } : undefined}
                        onViewDocuments={vendor.status === 'active' ? (v) => {
                          setSelectedVendor(v);
                          setActiveModal('documents');
                        } : undefined}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Performance Dashboard Modal */}
      {selectedVendor && (
        <Dialog open={activeModal === 'performance'} onOpenChange={(open: boolean) => setActiveModal(open ? 'performance' : null)}>
          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center space-x-3">
                <BarChart3 className="h-8 w-8 text-blue-600" />
                <span>Vendor Performance Dashboard</span>
              </DialogTitle>
            </DialogHeader>
            <VendorPerformanceDashboard
              vendor={selectedVendor}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Document Manager Modal */}
      {selectedVendor && (
        <Dialog open={activeModal === 'documents'} onOpenChange={(open: boolean) => setActiveModal(open ? 'documents' : null)}>
          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center space-x-3">
                <FileText className="h-8 w-8 text-green-600" />
                <span>Document Manager</span>
              </DialogTitle>
            </DialogHeader>
            <VendorDocumentManager
              vendor={selectedVendor}
              user={user}
              onUpdate={loadVendors}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Vendor Details Modal */}
      {selectedVendor && (
        <Dialog open={activeModal === 'details'} onOpenChange={(open: boolean) => setActiveModal(open ? 'details' : null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center space-x-3">
                <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                  {selectedVendor.name.charAt(0)}
                </div>
                <div>
                  <div>{selectedVendor.name}</div>
                  {selectedVendor.dbaName && (
                    <div className="text-sm font-normal text-gray-500">DBA: {selectedVendor.dbaName}</div>
                  )}
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 mt-6">
              {/* Status & Rating */}
              <div className="flex items-center justify-between pb-4 border-b">
                <div className="flex items-center space-x-3">
                  {getStatusBadge(selectedVendor.status)}
                  {selectedVendor.onboardedVia === 'self-service' && (
                    <Badge className="bg-purple-100 text-purple-700">Self-Onboarded</Badge>
                  )}
                </div>
                {getVendorRating(selectedVendor) && (
                  <div className="flex items-center space-x-2">
                    {getRatingStars(selectedVendor)}
                    <span className="text-sm text-gray-500">
                      ({getReviewCount(selectedVendor)} reviews)
                    </span>
                  </div>
                )}
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Contact Name</p>
                    <p className="font-medium">{selectedVendor.contactName}</p>
                    {selectedVendor.contactTitle && (
                      <p className="text-sm text-gray-500">{selectedVendor.contactTitle}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedVendor.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{selectedVendor.phone}</p>
                  </div>
                  {selectedVendor.alternatePhone && (
                    <div>
                      <p className="text-sm text-gray-500">Alternate Phone</p>
                      <p className="font-medium">{selectedVendor.alternatePhone}</p>
                    </div>
                  )}
                  {selectedVendor.website && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500">Website</p>
                      <a
                        href={selectedVendor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {selectedVendor.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                  Address
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {(() => {
                    const addr = getVendorAddressObject(selectedVendor);
                    if (!addr) return <p className="text-gray-600">No address on file</p>;
                    return (
                      <>
                        <p className="font-medium">{addr.street}</p>
                        {addr.street2 && (
                          <p className="text-gray-600">{addr.street2}</p>
                        )}
                        <p className="text-gray-600">
                          {addr.city}, {addr.state} {addr.zip}
                        </p>
                        <p className="text-gray-600">{addr.country}</p>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Services */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
                  Services Offered
                </h3>
                <div className="flex flex-wrap gap-2">
                  {getVendorServices(selectedVendor).map((service) => (
                    <Badge key={service} className="bg-blue-100 text-blue-700">
                      {service}
                    </Badge>
                  ))}
                </div>
                {selectedVendor.otherServiceDescription && (
                  <p className="mt-3 text-sm text-gray-600">{selectedVendor.otherServiceDescription}</p>
                )}
              </div>

              {/* Performance Metrics */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-blue-600" />
                  Performance
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Jobs Completed</p>
                    <p className="text-2xl font-bold text-green-700">
                      {selectedVendor.performanceMetrics.jobsCompleted}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Jobs In Progress</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {selectedVendor.performanceMetrics.jobsInProgress}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-purple-700">
                      ${selectedVendor.performanceMetrics.totalRevenue.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-sm text-gray-600">On-Time Completion Rate</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-600 rounded-full transition-all"
                          style={{ width: `${selectedVendor.performanceMetrics.onTimeCompletionRate}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">
                        {selectedVendor.performanceMetrics.onTimeCompletionRate}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Customer Satisfaction</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 rounded-full transition-all"
                          style={{ width: `${selectedVendor.performanceMetrics.customerSatisfaction}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">
                        {selectedVendor.performanceMetrics.customerSatisfaction}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Insurance & Compliance */}
              {(selectedVendor.generalLiability || selectedVendor.workersCompensation || selectedVendor.licenses.length > 0) && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-blue-600" />
                    Insurance & Licenses
                  </h3>
                  <div className="space-y-3">
                    {selectedVendor.generalLiability && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="font-medium">General Liability Insurance</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <div>
                            <span className="text-gray-600">Provider:</span> {selectedVendor.generalLiability.provider}
                          </div>
                          <div>
                            <span className="text-gray-600">Policy:</span> {selectedVendor.generalLiability.policyNumber}
                          </div>
                          <div>
                            <span className="text-gray-600">Coverage:</span> ${selectedVendor.generalLiability.coverageAmount?.toLocaleString()}
                          </div>
                          <div>
                            <span className="text-gray-600">Expires:</span> {new Date(selectedVendor.generalLiability.expirationDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedVendor.workersCompensation && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="font-medium">Workers' Compensation</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <div>
                            <span className="text-gray-600">Provider:</span> {selectedVendor.workersCompensation.provider}
                          </div>
                          <div>
                            <span className="text-gray-600">Policy:</span> {selectedVendor.workersCompensation.policyNumber}
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-600">Expires:</span> {new Date(selectedVendor.workersCompensation.expirationDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedVendor.licenses.length > 0 && (
                      <div>
                        <p className="font-medium mb-2">Licenses</p>
                        {selectedVendor.licenses.map((license, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-3 mb-2">
                            <p className="font-medium text-sm">{license.type}</p>
                            <p className="text-sm text-gray-600">
                              {license.number} - {license.state}
                            </p>
                            <p className="text-xs text-gray-500">
                              Expires: {new Date(license.expirationDate).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Terms */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-blue-600" />
                  Payment Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Payment Terms</p>
                    <p className="font-medium capitalize">{selectedVendor.paymentTerms.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Preferred Method</p>
                    <p className="font-medium capitalize">{selectedVendor.preferredPaymentMethod.replace(/_/g, ' ')}</p>
                  </div>
                  {selectedVendor.performanceMetrics.averageInvoiceAmount > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">Average Invoice</p>
                      <p className="font-medium">${selectedVendor.performanceMetrics.averageInvoiceAmount.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedVendor.notes && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                    Notes
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">{selectedVendor.notes}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                  <div>
                    <p>Created by {selectedVendor.createdByName}</p>
                    <p>{new Date(selectedVendor.createdAt).toLocaleDateString()}</p>
                  </div>
                  {selectedVendor.approvedBy && (
                    <div>
                      <p>Approved by {selectedVendor.approvedByName}</p>
                      <p>{selectedVendor.approvedAt && new Date(selectedVendor.approvedAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Analytics Dashboard Modal */}
      <Dialog open={activeModal === 'analytics'} onOpenChange={(open: boolean) => setActiveModal(open ? 'analytics' : null)}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vendor Analytics Dashboard</DialogTitle>
          </DialogHeader>
          <VendorAnalyticsDashboard
            vendors={vendors}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={activeModal === 'bulk-import'} onOpenChange={(open: boolean) => setActiveModal(open ? 'bulk-import' : null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Vendor Import</DialogTitle>
          </DialogHeader>
          <VendorBulkImport
            companyId={company.id}
            companyName={company.name}
            user={user}
            onComplete={(results) => {
              toast.success(`Import complete: ${results.successful} invitations sent`);
              setActiveModal(null);
              loadVendors();
            }}
            onCancel={() => setActiveModal(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Vendor Invite Modal */}
      <VendorInviteModal
        isOpen={activeModal === 'invite'}
        onClose={() => setActiveModal(null)}
        onSuccess={() => {
          toast.success('Vendor invitation sent successfully!');
          setActiveModal(null);
          loadVendors();
        }}
        company={company}
        user={user}
      />
    </div>
  );
}
